import { useMemo } from 'react';
// Third-party
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';

// Hooks
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppStore';

// Redux-toolkit
import { getAuthenticateUser } from '../../../store/authenticate/selectors';
import { getSheets } from '../../../store/sheets/selectors';
import { TTimeCardShift } from '../../../types';

const ClockShifts = () => {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const sheets = useAppSelector(getSheets);
  const { t } = useTranslation('clock');

  const userShifts = useMemo(() => {
    const userData = sheets.find((sheet) => sheet.personnel.email === personnel.email);

    const shifts = [...(userData?.shifts || [])]?.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());

    return shifts as Array<TTimeCardShift> || [];
  }, [sheets]);

  const calculateTimeDifference = (start: string, end: string) => {
    const startTime = moment(start);
    const endTime = moment(end);
    const diffMilliseconds = endTime.diff(startTime);
    const diffDuration = moment.duration(diffMilliseconds);
    const diffHours = diffDuration.hours();
    const diffMinutes = diffDuration.minutes();
    const diffSeconds = diffDuration.seconds();

    return (
      <Typography paddingTop={1} variant="caption">
        {diffHours.toString()?.padStart(2, '0')}
        :
        {diffMinutes.toString()?.padStart(2, '0')}
        :
        {diffSeconds.toString()?.padStart(2, '0')}
        {' '}
        {t('totalHrs')}
      </Typography>
    );
  };

  return (
    <List>
      <Stack spacing={2}>
        { userShifts?.map((shift) => (
          <Card key={shift.id} variant="outlined">
            <CardContent sx={{
              '&:last-child': { pb: 2 },
            }}
            >
              <Grid container>
                <Grid size={{
                  lg: 6, xs: 6, md: 6, sm: 6,
                }}
                >
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                    variant="body2"
                  >
                    {moment(shift.start).format('MMM Do YYYY')}
                  </Typography>
                  {calculateTimeDifference(shift.start, shift.end)}
                </Grid>
                <Grid size={{
                  lg: 6, xs: 6, md: 6, sm: 6,
                }}
                >
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                    variant="body2"
                  >
                    {t('inAndOut')}
                  </Typography>
                  <Typography paddingTop={1} variant="caption">
                    {moment(shift?.start).format('h:mm A')}
                    {' '}
                    ---
                    {' '}
                    {moment(shift?.end).format('h:mm A')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )) }
      </Stack>
    </List>
  );
};

export {
  ClockShifts,
};
